import { CustomerService, enumToExtendedArray, IApplicationFilter, PortfolioReportCache, property, widget, WidgetPropertyType } from '@dynamik/common';
import { InstrumentValuesReportViewModel } from '@dynamik/common/clients/reporting-clients';
import { BindingEngine, computedFrom } from 'aurelia-binding';
import { WidgetBase } from '@dynamik/common';
import { customElement } from 'aurelia-templating';
import { DisplayedFields } from '../displayed-fields';
import { CategoriesClient } from '@dynamik/common/clients/transaction-clients';
import { CategoryType } from '../../../classes/category-type';

export enum PortfolioPeriodGroupBy {
  None = 1,
  Type = 2,
  Category = 3,
  TermAndType = 4,
  TermAndCategory = 5
}

declare module '@dynamik/common/clients/reporting-clients' {
  interface InstrumentValuesReportViewModel {
    group1Header?: boolean;
    group1Total?: boolean;
    group2Header?: boolean;
    group2Total?: boolean;
    indent?: number;
  }
}

@customElement('portfolio-period-widget')
@widget('Portfolio period', true, false, 'Description')
export class PortfolioPeriodWidget extends WidgetBase {
  constructor(element: Element, customerService: CustomerService, private portfolioReportCache: PortfolioReportCache, bindingEngine: BindingEngine,
    private categoriesClient: CategoriesClient) {
    super(element, bindingEngine, customerService);
  }

  sourceData: InstrumentValuesReportViewModel[];
  allInstrumentValues: InstrumentValuesReportViewModel[];
  instrumentValues: InstrumentValuesReportViewModel[];
  total: InstrumentValuesReportViewModel;
  suppressSaveColumnState: boolean;
  currentPage = 0;
  lastItemRendered: (value: unknown) => void;
  DisplayedFields = DisplayedFields;

  @property('Data', undefined, WidgetPropertyType.List, enumToExtendedArray(PortfolioPeriodGroupBy))
  groupBy: PortfolioPeriodGroupBy = PortfolioPeriodGroupBy.None;

  @property('Data', 'Category type', WidgetPropertyType.List)
  categoryTypeId?: number;
  async categoryTypeIdListValues() {
    return this.customerService.instrumentCategories.filter(x => x.enabled).map(x => ({ value: x.catTypeId, label: x.catTypeName }));
  }

  @property('Data', 'Portfolio', WidgetPropertyType.List)
  portfolio?: string;
  async portfolioListValues() {
    return [
      { value: undefined, label: 'All portfolios' },
      ...(await this.categoriesClient.getSecCategories(this.customerService.selectedCustomer.companyId, CategoryType.Portfolio))
        .map(x => ({ value: x.name, label: x.name }))
    ];
  }
  portfolioChanged(newValue: string, oldValue: string) {
    if (!this.title) {
      return;
    }

    this.title = this.title.replace(` (${oldValue})`, '') + (newValue ? ` (${newValue})` : '');
  }

  @property('Data')
  quantityPrecision?: number = 0;

  @property('Data')
  precision: number = 2;

  @property('Table')
  font?: string;

  @property('Table')
  fontSize?: number = 10;

  @property('Table')
  color?: string;

  @property('Table')
  rowHeight?: number;

  @property('Table')
  rowsPerPage: number = 30;

  @property('Table')
  columnWidths?: string;

  @property()
  captionFont?: string;

  @property()
  captionFontSize?: number = 10;

  @property()
  captionColor?: string;

  protected override async loadData(filter: IApplicationFilter) {
    const values = await this.portfolioReportCache.instrumentValues(filter.customer.companyId, filter.dates.fromStr, filter.dates.toStr,
      [PortfolioPeriodGroupBy.Category, PortfolioPeriodGroupBy.TermAndCategory].includes(this.groupBy) ? this.categoryTypeId : undefined, this.portfolio, false);
    return values;
  }

  protected override async setData(data: InstrumentValuesReportViewModel[]) {
    var precisionLimit = Math.pow(0.1, this.precision);
    this.sourceData = data
    data = data.filter(x =>
      Math.abs(x.estimatedValueOpeningBalancePeriod) > precisionLimit
      || Math.abs(x.costOfAcquiredChangeInPeriod) > precisionLimit
      || Math.abs(x.realizedChangeInPeriod) > precisionLimit
      || Math.abs(x.realizedProfitLossChangeInPeriod) > precisionLimit
      || Math.abs(x.unrealizedProfitLossChangeInPeriod) > precisionLimit
      || Math.abs(x.estimatedValueClosingBalance) > precisionLimit
      || Math.abs(x.costPriceOpeningBalancePeriod) > precisionLimit
      || Math.abs(x.costPriceClosingBalance) > precisionLimit
    );
    this.total = this.getTotal(data);
    switch (this.groupBy) {
      case PortfolioPeriodGroupBy.Type:
      case PortfolioPeriodGroupBy.Category: {
        const groupByField = this.groupBy === PortfolioPeriodGroupBy.Type ? 'type' : 'category';
        const grouped = Array.from(data.groupBy(x => x[groupByField])).sort((a, b) => a[0].localeCompare(b[0]));
        data = [];
        for (const [key, groupData] of grouped) {
          const groupHeader = new InstrumentValuesReportViewModel();
          groupHeader.group1Header = true;
          groupHeader.name = key;
          groupHeader.indent = 0;
          data.push(groupHeader);
          groupData.forEach(x => x.indent = 10);
          data.push(...groupData.sort((a, b) => a.name.localeCompare(b.name)));
          const groupTotal = this.getTotal(groupData);
          groupTotal.name = `Total ${key}`;
          groupTotal.group1Total = true;
          groupTotal.indent = 0;
          data.push(groupTotal);
        }
        break;
      }
      case PortfolioPeriodGroupBy.TermAndType:
      case PortfolioPeriodGroupBy.TermAndCategory:
        const groupByField = this.groupBy === PortfolioPeriodGroupBy.TermAndType ? 'type' : 'category';
        const termGroups = Array.from(data.groupBy(x => x.longTerm ? 'Long Term' : 'Short Term')).sort((a, b) => a[0].localeCompare(b[0]));
        data = [];
        for (const [term, termData] of termGroups) {
          const termHeader = new InstrumentValuesReportViewModel();
          termHeader.group1Header = true;
          termHeader.name = term;
          termHeader.indent = 0;
          data.push(termHeader);
          const subGroups = Array.from(termData.groupBy(x => x[groupByField])).sort((a, b) => a[0].localeCompare(b[0]));
          for (const [subGroup, subGroupData] of subGroups) {
            const subHeader = new InstrumentValuesReportViewModel();
            subHeader.group2Header = true;
            subHeader.name = subGroup;
            subHeader.indent = 10;
            data.push(subHeader);
            subGroupData.forEach(x => x.indent = 20);
            data.push(...subGroupData.sort((a, b) => a.name.localeCompare(b.name)));
            const subTotal = this.getTotal(subGroupData);
            subTotal.name = `Total ${subGroup}`;
            subTotal.group2Total = true;
            subTotal.indent = 10;
            data.push(subTotal);
          }
          const termTotal = this.getTotal(termData);
          termTotal.name = `Total ${term}`;
          termTotal.group1Total = true;
          termTotal.indent = 0;
          data.push(termTotal);
        }
        break;
      default:
        data.sort((a, b) => a.name.localeCompare(b.name));
        break;
    }

    this.allInstrumentValues = data;
    this.allInstrumentValues.push(this.total);

    // Populate excelData for right-click export
    this.excelData = {
      fileName: 'PortfolioPeriodLight',
      data: { Items: this.sourceData }
    };

    this.updatePageData();
  }

  private updatePageData() {
    this.instrumentValues = this.allInstrumentValues.filter((x, i) => this.currentPage * this.rowsPerPage <= i && i < (this.currentPage + 1) * this.rowsPerPage);
  }

  getTotal(x: InstrumentValuesReportViewModel[]) {
    return x.reduce((p, c) => {
      p.estimatedValueOpeningBalancePeriod += c.estimatedValueOpeningBalancePeriod;
      p.costOfAcquiredChangeInPeriod += c.costOfAcquiredChangeInPeriod;
      p.realizedChangeInPeriod += c.realizedChangeInPeriod;
      p.realizedProfitLossChangeInPeriod += c.realizedProfitLossChangeInPeriod;
      p.unrealizedProfitLossChangeInPeriod += c.unrealizedProfitLossChangeInPeriod;
      p.estimatedValueClosingBalance += c.estimatedValueClosingBalance;
      p.costPriceOpeningBalancePeriod += c.costPriceOpeningBalancePeriod;
      p.costPriceClosingBalance += c.costPriceClosingBalance;
      return p;
    },
      new InstrumentValuesReportViewModel({
        name: 'TOTAL', estimatedValueOpeningBalancePeriod: 0, costOfAcquiredChangeInPeriod: 0, realizedProfitLossChangeInPeriod: 0, unrealizedProfitLossChangeInPeriod: 0, estimatedValueClosingBalance: 0,
        costPriceOpeningBalancePeriod: 0, costPriceClosingBalance: 0, realizedChangeInPeriod: 0
      }));
  }

  getPageCount(): number {
    return this.instrumentValues ? Math.ceil(this.allInstrumentValues.length / this.rowsPerPage) : 0;
  }

  async setPage(page: number): Promise<void> {
    if (page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.updatePageData();
    await new Promise(resolve => this.lastItemRendered = resolve);
  }

  override configChanged(key: keyof PortfolioPeriodWidget, newValue: any, oldValue: any) {
    switch (key) {
      case 'rowsPerPage':
        this.updatePageData();
        break
      case 'portfolio':
        this.portfolioChanged(newValue, oldValue);
      case 'quantityPrecision':
      case 'groupBy':
      case 'categoryTypeId':
      case 'precision':
        this.refresh$.next();
        break;
      default:
        break;
    }
  }
}
